import {
  Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, InputAdornment,
  ListItemText, MenuItem, Select, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { Search, FilterAltOff } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { TEST_STATUSES, TEST_STATUS_COLORS } from '../../constants/testStatusColors';

interface TestsFilterBarProps {
  onSearchChange: (value: string) => void;
  selectedStatuses: string[];
  onToggleStatus: (status: string) => void;
  onClearStatuses: () => void;
  statusCounts: Record<string, number>;
  totalCount: number;
  testTypes: string[];
  selectedTypes: string[];
  onTypesChange: (types: string[]) => void;
  /** Entity filters (options derived from the loaded rows). */
  projects: string[];
  projectFilter: string | null;
  onProjectChange: (value: string | null) => void;
  workOrders: string[];
  workOrderFilter: string | null;
  onWorkOrderChange: (value: string | null) => void;
  quotes: string[];
  quoteFilter: string | null;
  onQuoteChange: (value: string | null) => void;
  /** E2E integrity sandbox rows are hidden unless this is on. */
  showValidation: boolean;
  onToggleValidation: (show: boolean) => void;
  onClearAll: () => void;
  hasAnyFilter: boolean;
}

const entityFilterSx = {
  minWidth: 170,
  '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem', py: '1px' },
  '& .MuiInputLabel-root': { fontSize: '0.8rem' },
};

/** Search + entity filters (project / WO / quote) + status chips + test-type
 *  multiselect + validation-rows toggle for the Tests page. */
export const TestsFilterBar = ({
  onSearchChange, selectedStatuses, onToggleStatus, onClearStatuses, statusCounts,
  totalCount, testTypes, selectedTypes, onTypesChange,
  projects, projectFilter, onProjectChange,
  workOrders, workOrderFilter, onWorkOrderChange,
  quotes, quoteFilter, onQuoteChange,
  showValidation, onToggleValidation,
  onClearAll, hasAnyFilter,
}: TestsFilterBarProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <TextField
        size="small"
        placeholder="Search sample, project, WO, quote…"
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{ width: 250, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }}
      />
      <Autocomplete
        size="small"
        options={projects}
        value={projectFilter}
        onChange={(_e, v) => onProjectChange(v)}
        renderInput={(params) => <TextField {...params} label="Project" />}
        sx={entityFilterSx}
      />
      <Autocomplete
        size="small"
        options={workOrders}
        value={workOrderFilter}
        onChange={(_e, v) => onWorkOrderChange(v)}
        renderInput={(params) => <TextField {...params} label="Work Order" />}
        sx={{ ...entityFilterSx, minWidth: 150 }}
      />
      <Autocomplete
        size="small"
        options={quotes}
        value={quoteFilter}
        onChange={(_e, v) => onQuoteChange(v)}
        renderInput={(params) => <TextField {...params} label="Quote" />}
        sx={{ ...entityFilterSx, minWidth: 140 }}
      />
      <Select
        multiple
        size="small"
        displayEmpty
        value={selectedTypes}
        onChange={(e) => onTypesChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
        renderValue={(selected) => (selected.length === 0
          ? <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>All test types</Typography>
          : <Typography sx={{ fontSize: '0.82rem' }}>{selected.length === 1 ? selected[0] : `${selected.length} test types`}</Typography>)}
        sx={{ minWidth: 170, borderRadius: 2, fontSize: '0.82rem' }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 340 } } }}
      >
        {testTypes.map((t) => (
          <MenuItem key={t} value={t} dense>
            <Checkbox size="small" checked={selectedTypes.includes(t)} sx={{ p: 0.5, mr: 0.5 }} />
            <ListItemText primary={t} primaryTypographyProps={{ fontSize: '0.82rem' }} />
          </MenuItem>
        ))}
      </Select>
      {hasAnyFilter && (
        <Button
          size="small"
          startIcon={<FilterAltOff sx={{ fontSize: 16 }} />}
          onClick={onClearAll}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Clear filters
        </Button>
      )}
      <Box sx={{ flex: 1 }} />
      <Tooltip title="Show the App Integrity validation sandbox tests (E2E projects — hidden by default)">
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={showValidation}
              onChange={(e) => onToggleValidation(e.target.checked)}
            />
          )}
          label="Validation tests"
          slotProps={{ typography: { sx: { fontSize: '0.75rem', color: 'text.secondary' } } }}
          sx={{ mr: 0 }}
        />
      </Tooltip>
    </Box>

    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      <Chip
        label={`All (${totalCount})`}
        size="small"
        clickable
        variant={selectedStatuses.length === 0 ? 'filled' : 'outlined'}
        onClick={onClearStatuses}
        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
      />
      {TEST_STATUSES.map((s) => {
        const active = selectedStatuses.includes(s);
        const color = TEST_STATUS_COLORS[s];
        return (
          <Chip
            key={s}
            size="small"
            clickable
            variant={active ? 'filled' : 'outlined'}
            label={`${s} (${statusCounts[s] || 0})`}
            onClick={() => onToggleStatus(s)}
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              ...(active && { backgroundColor: alpha(color, 0.15), color, borderColor: color }),
              ...(!active && { borderColor: alpha(color, 0.3), color }),
            }}
          />
        );
      })}
    </Box>
  </Box>
);
